/**
 * Shared rule for when a payment must clear immediately.
 *
 * ACH (`us_bank_account`) takes 3–5 business days to settle. When coverage
 * starts sooner than that, the policy would not be paid for on its start
 * date, so we require a credit/debit card instead.
 *
 * Used by both the SDA purchase flow (first payment) and policy renewals,
 * on the frontend to disable bank payment methods and on the backend to
 * reject them. Keep the two in sync by importing from here rather than
 * re-declaring the threshold.
 */
export const CARD_ONLY_THRESHOLD_DAYS = 5;

/**
 * Flat fee charged when a renewal is paid after its term has already begun.
 * Dollars.
 */
export const RENEWAL_LATE_FEE = 25;

/**
 * How many days before a term starts the automatic renewal charge runs.
 *
 * This is the cron's selection window: `ProcessRenewalsTask` picks up terms
 * whose `startsOn` falls within the next RENEWAL_WINDOW_DAYS, so in practice
 * a renewal is charged up to this many days ahead of the start date.
 *
 * Shared because it is customer-facing — the tenant dashboard tells people
 * when the charge will land, and the support assistant repeats it. It was
 * previously hardcoded as 7 on the dashboard while the cron used 14, so
 * tenants were told the wrong date. Import from here; don't re-declare it.
 *
 * Must stay equal to `ProcessRenewalsTask.RENEWAL_WINDOW_DAYS`.
 */
export const RENEWAL_WINDOW_DAYS = 14;

/**
 * Parse a YYYY-MM-DD DATE-only string as local midnight.
 *
 * Deliberately avoids `new Date("YYYY-MM-DD")`, which parses as UTC and
 * shifts to the previous calendar day in US timezones — that would make the
 * day count off by one near the boundary. Mirrors `parseLocalDate` in
 * `@ui/shared`; duplicated here so this module stays dependency-free and
 * usable from backend services.
 */
function parseDateOnlyLocal(dateString: string): Date {
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
    const [year, month, day] = dateString.split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  return new Date(dateString);
}

/**
 * Whole days from now until `startDate`. Negative once the date has passed.
 * Returns null when the input is missing or unparseable, so callers can
 * decide what an unknown start date means for them.
 */
export function daysUntilStart(
  startDate: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!startDate) return null;
  const start = parseDateOnlyLocal(startDate);
  if (Number.isNaN(start.getTime())) return null;
  const msPerDay = 24 * 60 * 60 * 1000;
  return Math.floor((start.getTime() - now.getTime()) / msPerDay);
}

/**
 * True when coverage starts too soon for ACH to clear (or has already
 * started), meaning the payer must use a card.
 *
 * An absent/unparseable start date returns false — we don't restrict the
 * payer on incomplete data; the charge itself still has to succeed.
 */
export function requiresImmediateClearing(
  startDate: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const days = daysUntilStart(startDate, now);
  if (days === null) return false;
  return days < CARD_ONLY_THRESHOLD_DAYS;
}

/**
 * The payment method type allowed for a payment, given when coverage starts.
 *
 *   "card" — starts in < CARD_ONLY_THRESHOLD_DAYS days (or already started).
 *            ACH wouldn't clear in time.
 *   "bank" — starts further out. ACH has time to settle, and it's cheaper
 *            to process and can't be charged back the way a card can.
 *   null   — start date unknown; impose no restriction rather than guess.
 *
 * The two windows are complementary: exactly one method type is valid at any
 * given moment, with no gap where a payer has no option.
 *
 * Applies to both the SDA purchase (first payment) and renewals, on the
 * frontend to filter selectable methods and on the backend to reject the
 * rest. The renewal CRON deliberately bends this — see
 * `pickRenewalPaymentMethodWithFallback` — because skipping a charge over a
 * method type would cost the tenant their coverage.
 */
export function allowedPaymentMethodType(
  startDate: string | null | undefined,
  now: Date = new Date(),
): "card" | "bank" | null {
  const days = daysUntilStart(startDate, now);
  if (days === null) return null;
  return days < CARD_ONLY_THRESHOLD_DAYS ? "card" : "bank";
}

/**
 * Whether a saved payment method may be used for a payment starting on
 * `startDate`. `us_bank_account` and legacy `bank_account` both count as
 * bank; everything else is treated as a card.
 */
export function isPaymentMethodAllowedForStart(
  methodType: string | null | undefined,
  startDate: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const allowed = allowedPaymentMethodType(startDate, now);
  if (allowed === null) return true;
  const isBank =
    methodType === "us_bank_account" || methodType === "bank_account";
  return allowed === "bank" ? isBank : !isBank;
}

/**
 * True when a renewal term has already started, so paying it now incurs the
 * late fee.
 *
 * Strictly `< 0` days: paying ON the start date is on time. An absent or
 * unparseable date returns false — we never invent a fee from incomplete
 * data.
 */
export function isRenewalLate(
  startDate: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const days = daysUntilStart(startDate, now);
  if (days === null) return false;
  return days < 0;
}

/**
 * The late fee owed on a renewal, in dollars — `RENEWAL_LATE_FEE` once the
 * term has started, otherwise 0.
 *
 * Shared deliberately: the tenant-frontend renewal page calls this to show
 * the fee before payment, and `RenewalPaymentService.payRenewal` calls it to
 * compute the charge. One rule means the amount displayed and the amount
 * billed cannot drift apart.
 *
 * Charged once, at payment time — `policyTerm.lateFeeAppliedAt` records that
 * it happened, so it can't be applied twice.
 */
export function renewalLateFee(
  startDate: string | null | undefined,
  now: Date = new Date(),
): number {
  return isRenewalLate(startDate, now) ? RENEWAL_LATE_FEE : 0;
}

/**
 * The late fee actually payable on a renewal, accounting for the two ways it
 * can be suppressed:
 *   - `lateFeeWaived`    — an admin waived it.
 *   - `lateFeeAppliedAt` — already charged; never stacked a second time.
 *
 * Mirrors the conditions in `RenewalPaymentService.payRenewal`, so the figure
 * the tenant sees on the renewal page matches what gets billed. Prefer this
 * over bare `renewalLateFee` anywhere a term's state is available.
 */
export function payableRenewalLateFee(
  term:
    | {
        startsOn?: string | null;
        lateFeeWaived?: boolean | null;
        lateFeeAppliedAt?: string | Date | null;
      }
    | null
    | undefined,
  now: Date = new Date(),
): number {
  if (!term) return 0;
  if (term.lateFeeWaived === true) return 0;
  if (term.lateFeeAppliedAt != null) return 0;
  return renewalLateFee(term.startsOn, now);
}

/**
 * The admin credit still spendable against a renewal.
 *
 * Zero once `creditAppliedAt` is set: the credit has been consumed by a
 * charge, and showing it again would tell the tenant they owe less than
 * they do. Mirrors the guard in `RenewalPaymentService.payRenewal`.
 *
 * This is the GRANTED figure; it is not clamped here because the amount owed
 * isn't known at this level. Callers clamp against their own total — see
 * `renewalAmountDue`.
 */
export function payableRenewalCredit(
  term:
    | {
        creditAmount?: number | string | null;
        creditAppliedAt?: string | Date | null;
      }
    | null
    | undefined,
): number {
  if (!term) return 0;
  if (term.creditAppliedAt != null) return 0;
  const amount = Number(term.creditAmount ?? 0);
  return Number.isFinite(amount) && amount > 0 ? amount : 0;
}

/**
 * What a tenant actually pays for a renewal, in the same order the server
 * computes it: base + late fee − credit, clamped at zero.
 *
 * One helper so the renewal page, the ACH disclosure and the charge can't
 * disagree about the figure. `credit` is what was actually spent, which a
 * caller wanting to show the breakdown needs — a $500 credit against a $200
 * renewal spends $200, not $500.
 */
export function renewalAmountDue(
  baseAmount: number,
  lateFee: number,
  grantedCredit: number,
): { gross: number; credit: number; due: number } {
  const gross = baseAmount + lateFee;
  const credit = Math.min(Math.max(grantedCredit, 0), gross);
  return { gross, credit, due: gross - credit };
}


/**
 * The date the automatic renewal charge is expected to run for a term
 * starting on `startDate` — RENEWAL_WINDOW_DAYS before it.
 *
 * Returns null when the date is missing or unparseable, so callers can show
 * their own placeholder.
 */
export function renewalProcessingDate(
  startDate: string | null | undefined,
): Date | null {
  if (!startDate) return null;
  const d = parseDateOnlyLocal(startDate);
  if (isNaN(d.getTime())) return null;
  d.setDate(d.getDate() - RENEWAL_WINDOW_DAYS);
  return d;
}
